import type {
  Project,
  SavedViewGroupByT,
  SavedViewSort,
  SmartList,
  SortDirectionT,
  SubtaskDisplayT,
  Task,
  TaskArrangement,
  TaskWorkflowStateT,
  WorkflowColumn,
} from '@portfolio-pm/shared';
import { DEFAULT_WORKFLOW_COLUMNS } from '@portfolio-pm/shared';
import { compileQuery } from '@portfolio-pm/smart-list';

export type TaskSystemViewId = 'all' | 'today' | 'tomorrow' | 'week' | 'favorites' | 'trash';
export type TaskDisplayMode = 'list' | 'board' | 'calendar' | 'timeline';
export const TASK_DISPLAY_MODES: TaskDisplayMode[] = ['list', 'board', 'calendar', 'timeline'];
export type TaskCompletionFilter = 'incomplete' | 'completed' | 'both';
export type TaskGroupBy = SavedViewGroupByT;
export type TaskSort = SavedViewSort;
export type TaskSortDirection = SortDirectionT;
export type TaskArrangementView = Pick<
  TaskArrangement,
  'id' | 'name' | 'groupBy' | 'groupDirection' | 'sort'
> & {
  builtIn?: boolean;
};

export const DEFAULT_TASK_ARRANGEMENT_ID = 'builtin:default';

export const DEFAULT_TASK_ARRANGEMENT: TaskArrangementView = {
  id: DEFAULT_TASK_ARRANGEMENT_ID,
  name: 'Default',
  builtIn: true,
  groupBy: 'project',
  groupDirection: 'asc',
  sort: [
    { key: 'due', direction: 'asc' },
    { key: 'priority', direction: 'desc' },
  ],
};

export const BUILT_IN_TASK_ARRANGEMENTS: TaskArrangementView[] = [
  DEFAULT_TASK_ARRANGEMENT,
  {
    id: 'builtin:manual',
    name: 'Manual order',
    builtIn: true,
    groupBy: 'none',
    groupDirection: 'asc',
    sort: [{ key: 'manual', direction: 'asc' }],
  },
  {
    id: 'builtin:priority',
    name: 'Priority',
    builtIn: true,
    groupBy: 'none',
    groupDirection: 'asc',
    sort: [
      { key: 'priority', direction: 'desc' },
      { key: 'due', direction: 'asc' },
    ],
  },
  {
    id: 'builtin:due',
    name: 'Due date',
    builtIn: true,
    groupBy: 'none',
    groupDirection: 'asc',
    sort: [
      { key: 'due', direction: 'asc' },
      { key: 'priority', direction: 'desc' },
    ],
  },
  {
    id: 'builtin:scheduled',
    name: 'Scheduled date',
    builtIn: true,
    groupBy: 'none',
    groupDirection: 'asc',
    sort: [
      { key: 'scheduled', direction: 'asc' },
      { key: 'priority', direction: 'desc' },
    ],
  },
  {
    id: 'builtin:estimate',
    name: 'Estimated length',
    builtIn: true,
    groupBy: 'none',
    groupDirection: 'asc',
    sort: [
      { key: 'estimate', direction: 'asc' },
      { key: 'due', direction: 'asc' },
    ],
  },
];

export type TaskWorkspaceView =
  | { kind: 'system'; id: TaskSystemViewId }
  | { kind: 'project'; projectId: string }
  | { kind: 'smart'; smartListId: string }
  | { kind: 'tag'; tag: string };

export function getTaskWorkspaceListKey(view: TaskWorkspaceView): string {
  if (view.kind === 'project') return `project:${view.projectId}`;
  if (view.kind === 'system') return `system:${view.id}`;
  if (view.kind === 'smart') return `smart:${view.smartListId}`;
  return `tag:${view.tag}`;
}

export interface TaskWorkspaceGroup {
  id: string;
  label: string;
  tasks: Task[];
  depthByTaskId?: Map<string, number>;
}

export interface TaskWorkspaceCounts {
  system: Record<TaskSystemViewId, number>;
  projects: Map<string, number>;
  smartLists: Map<string, number>;
  tags: Map<string, number>;
}

export const TASK_WORKFLOW_STATES: TaskWorkflowStateT[] = [
  ...DEFAULT_WORKFLOW_COLUMNS.map((column) => column.id),
];

export function getTaskWorkspaceLabel(
  view: TaskWorkspaceView,
  projectsById: Map<string, Project>,
  smartListsById = new Map<string, SmartList>(),
): string {
  if (view.kind === 'system') {
    switch (view.id) {
      case 'all':
        return 'All Tasks';
      case 'today':
        return 'Today';
      case 'tomorrow':
        return 'Tomorrow';
      case 'week':
        return 'This Week';
      case 'favorites':
        return 'Favourites';
      case 'trash':
        return 'Trash';
    }
  }

  if (view.kind === 'smart') {
    return smartListsById.get(view.smartListId)?.name ?? 'Saved View';
  }

  if (view.kind === 'tag') return `#${view.tag}`;

  return projectsById.get(view.projectId)?.name ?? 'Project';
}

export function getTaskWorkspaceDescription(view: TaskWorkspaceView): string {
  if (view.kind === 'project') {
    return 'Project tasks with a persistent detail inspector.';
  }
  if (view.kind === 'smart') {
    return 'Saved task filter with live results across your workspace.';
  }
  if (view.kind === 'tag') return 'Tagged tasks across your workspace.';

  switch (view.id) {
    case 'all':
      return 'All open work across your active projects.';
    case 'today':
      return 'Tasks scheduled or due today.';
    case 'tomorrow':
      return 'Tasks scheduled or due tomorrow.';
    case 'week':
      return 'Tasks scheduled or due in the next seven days.';
    case 'favorites':
      return 'Starred tasks you want close to hand.';
    case 'trash':
      return 'Tasks moved to Trash before permanent removal.';
  }
}

export function getTaskDisplayModeLabel(mode: TaskDisplayMode): string {
  switch (mode) {
    case 'list':
      return 'List';
    case 'board':
      return 'Board';
    case 'calendar':
      return 'Calendar';
    case 'timeline':
      return 'Timeline';
  }
}

export function isTaskDisplayMode(value: string | null | undefined): value is TaskDisplayMode {
  return (
    value !== undefined && value !== null && TASK_DISPLAY_MODES.includes(value as TaskDisplayMode)
  );
}

export function getTaskWorkflowStateLabel(
  state: TaskWorkflowStateT,
  workflowColumns: WorkflowColumn[] = [],
): string {
  return (
    workflowColumns.find((column) => column.id === state)?.name ??
    DEFAULT_WORKFLOW_COLUMNS.find((column) => column.id === state)?.name ??
    state
  );
}

export function filterTasksForTaskView(
  tasks: Task[],
  view: TaskWorkspaceView,
  now: number,
  smartLists: SmartList[] = [],
  projects: Project[] = [],
): Task[] {
  const visibleTasks = filterArchivedProjectTasksForView(tasks, view, projects);
  if (view.kind === 'smart') {
    const smartList = smartLists.find((candidate) => candidate.id === view.smartListId);
    if (!smartList) return [];
    return filterTasksByQuery(visibleTasks, smartList.query, now);
  }

  return visibleTasks.filter((task) => matchesTaskView(task, view, now));
}

export function buildTaskWorkspaceCounts(
  tasks: Task[],
  projects: Project[],
  smartLists: SmartList[],
  now: number,
): TaskWorkspaceCounts {
  const activeProjectIds = getActiveProjectIds(projects);
  const openTasks = tasks.filter(
    (task) =>
      task.completedAt === undefined &&
      task.trashedAt === undefined &&
      isTaskInActiveProject(task, activeProjectIds),
  );
  const trashedTasks = tasks.filter(
    (task) => task.trashedAt !== undefined && isTaskInActiveProject(task, activeProjectIds),
  );
  const projectsCount = new Map<string, number>();
  const tagsCount = new Map<string, number>();
  for (const project of projects) {
    projectsCount.set(project.id, openTasks.filter((task) => task.projectId === project.id).length);
  }
  for (const task of openTasks) {
    for (const tag of task.tags) tagsCount.set(tag, (tagsCount.get(tag) ?? 0) + 1);
  }

  return {
    system: {
      all: openTasks.length,
      today: openTasks.filter((task) => matchesTaskView(task, { kind: 'system', id: 'today' }, now))
        .length,
      tomorrow: openTasks.filter((task) =>
        matchesTaskView(task, { kind: 'system', id: 'tomorrow' }, now),
      ).length,
      week: openTasks.filter((task) => matchesTaskView(task, { kind: 'system', id: 'week' }, now))
        .length,
      favorites: openTasks.filter((task) =>
        matchesTaskView(task, { kind: 'system', id: 'favorites' }, now),
      ).length,
      trash: trashedTasks.length,
    },
    projects: projectsCount,
    smartLists: new Map(
      smartLists.map((smartList) => [
        smartList.id,
        filterTasksByQuery(
          filterTasksForArchivedProjects(tasks, projects),
          smartList.query,
          now,
        ).filter((task) => task.trashedAt === undefined || smartList.showTrashed).length,
      ]),
    ),
    tags: tagsCount,
  };
}

export function buildTaskWorkspaceGroups({
  tasks,
  projects,
  smartLists,
  view,
  completionFilter,
  now,
  groupBy,
  groupDirection,
  sort,
  subtaskDisplay = 'flat',
}: {
  tasks: Task[];
  projects: Project[];
  smartLists: SmartList[];
  view: TaskWorkspaceView;
  completionFilter: TaskCompletionFilter;
  now: number;
  groupBy?: TaskGroupBy | undefined;
  groupDirection?: TaskSortDirection | undefined;
  sort?: TaskSort[] | undefined;
  subtaskDisplay?: SubtaskDisplayT | undefined;
}): TaskWorkspaceGroup[] {
  const filtered = filterTasksForTaskView(tasks, view, now, smartLists, projects).filter((task) => {
    if (view.kind === 'smart') {
      const smartList = smartLists.find((candidate) => candidate.id === view.smartListId);
      return smartList?.showTrashed ? true : task.trashedAt === undefined;
    }
    if (view.kind === 'system' && view.id === 'trash') return task.trashedAt !== undefined;
    return task.trashedAt === undefined;
  });
  const actualGroupBy =
    groupBy ??
    (view.kind === 'project'
      ? 'none'
      : view.kind === 'smart'
        ? getSmartListGroupBy(view, smartLists)
        : 'project');
  const actualSort =
    sort ??
    (view.kind === 'smart'
      ? getSmartListSort(view, smartLists)
      : [{ key: 'due', direction: 'asc' }]);
  const actualDirection =
    groupDirection ??
    (view.kind === 'smart' ? getSmartListGroupDirection(view, smartLists) : 'asc');
  const visibleTasks = filtered.filter((task) =>
    matchesTaskCompletionFilter(task, completionFilter),
  );

  if (subtaskDisplay === 'nested') {
    return buildNestedTaskGroups({
      tasks: visibleTasks,
      projects,
      groupBy: actualGroupBy,
      groupDirection: actualDirection,
      sort: actualSort,
    });
  }

  if (actualGroupBy === 'none') {
    const openTasks = visibleTasks
      .filter((task) => task.completedAt === undefined)
      .sort((a, b) => compareTasksBySort(a, b, actualSort));
    const completedTasks = visibleTasks
      .filter((task) => task.completedAt !== undefined)
      .sort((a, b) => compareTasksBySort(a, b, actualSort));

    return [
      ...(completionFilter !== 'completed'
        ? [{ id: 'open', label: 'Open', tasks: openTasks }]
        : []),
      ...(completionFilter !== 'incomplete'
        ? [{ id: 'completed', label: 'Completed', tasks: completedTasks }]
        : []),
    ].filter((group) => group.tasks.length > 0);
  }

  const projectNames = new Map(projects.map((project) => [project.id, project.name]));
  const grouped = new Map<string, Task[]>();

  for (const task of visibleTasks) {
    for (const groupKey of getTaskGroupKeys(task, actualGroupBy)) {
      const bucket = grouped.get(groupKey) ?? [];
      bucket.push(task);
      grouped.set(groupKey, bucket);
    }
  }

  return [...grouped.entries()]
    .sort((left, right) => {
      const result = compareGroupKeys(left[0], right[0], actualGroupBy, projectNames);
      return actualDirection === 'asc' ? result : -result;
    })
    .map(([projectId, bucket]) => ({
      id: `${actualGroupBy}:${projectId}`,
      label: getGroupLabel(projectId, actualGroupBy, projects),
      tasks: bucket.sort((a, b) => compareTasksBySort(a, b, actualSort)),
    }))
    .filter((group) => group.tasks.length > 0);
}

export function buildNestedTaskGroups({
  tasks,
  projects,
  groupBy,
  groupDirection,
  sort,
}: {
  tasks: Task[];
  projects: Project[];
  groupBy: TaskGroupBy;
  groupDirection: TaskSortDirection;
  sort: TaskSort[];
}): TaskWorkspaceGroup[] {
  const tasksById = new Map(tasks.map((task) => [task.id, task]));
  const rootByTaskId = new Map<string, Task>();

  for (const task of tasks) {
    rootByTaskId.set(task.id, findVisibleRoot(task, tasksById));
  }

  const childrenByParentId = new Map<string, Task[]>();
  for (const task of tasks) {
    const parent = task.parentTaskId ? tasksById.get(task.parentTaskId) : undefined;
    if (!parent || rootByTaskId.get(parent.id)?.id !== rootByTaskId.get(task.id)?.id) continue;
    const children = childrenByParentId.get(parent.id) ?? [];
    children.push(task);
    childrenByParentId.set(parent.id, children);
  }
  for (const children of childrenByParentId.values()) {
    children.sort((left, right) => compareTasksBySort(left, right, sort));
  }

  const roots = [
    ...new Map([...rootByTaskId.values()].map((task) => [task.id, task])).values(),
  ].sort((left, right) => compareTasksBySort(left, right, sort));
  const projectNames = new Map(projects.map((project) => [project.id, project.name]));
  const groupedRoots = new Map<string, Task[]>();

  for (const root of roots) {
    const keys =
      groupBy === 'none'
        ? [root.completedAt === undefined ? 'open' : 'completed']
        : getTaskGroupKeys(root, groupBy);
    for (const key of keys) {
      const bucket = groupedRoots.get(key) ?? [];
      bucket.push(root);
      groupedRoots.set(key, bucket);
    }
  }

  return [...groupedRoots.entries()]
    .sort((left, right) => {
      if (groupBy === 'none') {
        const result = left[0] === right[0] ? 0 : left[0] === 'open' ? -1 : 1;
        return groupDirection === 'asc' ? result : -result;
      }
      const result = compareGroupKeys(left[0], right[0], groupBy, projectNames);
      return groupDirection === 'asc' ? result : -result;
    })
    .map(([key, groupRoots]) => {
      const nestedTasks: Task[] = [];
      const depthByTaskId = new Map<string, number>();
      const visited = new Set<string>();
      for (const root of groupRoots) {
        appendTaskFamily(root, 0, childrenByParentId, nestedTasks, depthByTaskId, visited);
      }
      return {
        id: groupBy === 'none' ? key : `${groupBy}:${key}`,
        label:
          groupBy === 'none'
            ? key === 'open'
              ? 'Open'
              : 'Completed'
            : getGroupLabel(key, groupBy, projects),
        tasks: nestedTasks,
        depthByTaskId,
      };
    })
    .filter((group) => group.tasks.length > 0);
}

function findVisibleRoot(task: Task, tasksById: Map<string, Task>): Task {
  let current = task;
  const seen = new Set<string>([task.id]);
  while (current.parentTaskId) {
    const parent = tasksById.get(current.parentTaskId);
    if (!parent || seen.has(parent.id)) return task;
    seen.add(parent.id);
    current = parent;
  }
  return current;
}

function appendTaskFamily(
  task: Task,
  depth: number,
  childrenByParentId: Map<string, Task[]>,
  output: Task[],
  depthByTaskId: Map<string, number>,
  visited: Set<string>,
): void {
  if (visited.has(task.id)) return;
  visited.add(task.id);
  output.push(task);
  depthByTaskId.set(task.id, depth);
  for (const child of childrenByParentId.get(task.id) ?? []) {
    appendTaskFamily(child, depth + 1, childrenByParentId, output, depthByTaskId, visited);
  }
}

export function matchesTaskCompletionFilter(
  task: Task,
  completionFilter: TaskCompletionFilter,
): boolean {
  if (completionFilter === 'both') return true;
  return completionFilter === 'completed'
    ? task.completedAt !== undefined
    : task.completedAt === undefined;
}

export function matchesTaskView(task: Task, view: TaskWorkspaceView, now: number): boolean {
  if (view.kind === 'smart') return false;

  if (view.kind === 'project') {
    return task.projectId === view.projectId;
  }

  if (view.kind === 'tag') return task.tags.includes(view.tag);

  switch (view.id) {
    case 'all':
      return true;
    case 'today':
      return hasTaskDateInRange(task, dayRange(now, 0));
    case 'tomorrow':
      return hasTaskDateInRange(task, dayRange(now, 1));
    case 'week':
      return hasTaskDateInRange(task, weekRange(now));
    case 'favorites':
      return task.favorite === true;
    case 'trash':
      return task.trashedAt !== undefined;
  }
}

function filterTasksByQuery(tasks: Task[], query: string, now: number): Task[] {
  const childTaskIds = new Set(
    tasks
      .filter((task) => tasks.some((candidate) => candidate.parentTaskId === task.id))
      .map((task) => task.id),
  );
  const compiled = compileQuery(query, { now: new Date(now), childTaskIds });
  if ('error' in compiled) return [];
  return tasks.filter((task) => compiled.match(task));
}

function filterArchivedProjectTasksForView(
  tasks: Task[],
  view: TaskWorkspaceView,
  projects: Project[],
): Task[] {
  if (view.kind === 'project') return tasks;
  return filterTasksForArchivedProjects(tasks, projects);
}

function filterTasksForArchivedProjects(tasks: Task[], projects: Project[]): Task[] {
  const activeProjectIds = getActiveProjectIds(projects);
  return tasks.filter((task) => isTaskInActiveProject(task, activeProjectIds));
}

function getActiveProjectIds(projects: Project[]): Set<string> | undefined {
  if (projects.length === 0) return undefined;
  return new Set(projects.filter((project) => !project.archived).map((project) => project.id));
}

function isTaskInActiveProject(task: Task, activeProjectIds: Set<string> | undefined): boolean {
  return activeProjectIds === undefined || activeProjectIds.has(task.projectId);
}

export function compareTasks(left: Task, right: Task): number {
  if ((left.completedAt === undefined) !== (right.completedAt === undefined)) {
    return left.completedAt === undefined ? -1 : 1;
  }

  const leftDate = Math.min(
    left.dueAt ?? Number.MAX_SAFE_INTEGER,
    left.scheduledAt ?? Number.MAX_SAFE_INTEGER,
  );
  const rightDate = Math.min(
    right.dueAt ?? Number.MAX_SAFE_INTEGER,
    right.scheduledAt ?? Number.MAX_SAFE_INTEGER,
  );

  if (leftDate !== rightDate) return leftDate - rightDate;
  if (left.priority !== right.priority) {
    return prioritySortValue(right.priority) - prioritySortValue(left.priority);
  }
  return left.createdAt - right.createdAt;
}

export function compareTasksBySort(left: Task, right: Task, sort: TaskSort[]): number {
  for (const rule of sort.length > 0 ? sort : [{ key: 'due', direction: 'asc' } as TaskSort]) {
    const result = compareTaskSortKey(left, right, rule.key);
    if (result !== 0) return rule.direction === 'asc' ? result : -result;
  }
  return compareTasks(left, right);
}

export function getTaskCalendarAnchor(task: Task): number | undefined {
  return task.scheduledAt ?? task.dueAt;
}

function dayRange(now: number, offset: number): [number, number] {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() + offset);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return [start.getTime(), end.getTime()];
}

function weekRange(now: number): [number, number] {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 7);
  return [start.getTime(), end.getTime()];
}

function hasTaskDateInRange(task: Task, [start, end]: [number, number]): boolean {
  return [task.dueAt, task.scheduledAt].some(
    (value) => value !== undefined && value >= start && value < end,
  );
}

function getSmartListGroupBy(view: TaskWorkspaceView, smartLists: SmartList[]): TaskGroupBy {
  if (view.kind !== 'smart') return 'project';
  return smartLists.find((candidate) => candidate.id === view.smartListId)?.groupBy ?? 'project';
}

function getSmartListGroupDirection(
  view: TaskWorkspaceView,
  smartLists: SmartList[],
): TaskSortDirection {
  if (view.kind !== 'smart') return 'asc';
  return smartLists.find((candidate) => candidate.id === view.smartListId)?.groupDirection ?? 'asc';
}

function getSmartListSort(view: TaskWorkspaceView, smartLists: SmartList[]): TaskSort[] {
  if (view.kind !== 'smart') return [{ key: 'due', direction: 'asc' }];
  return smartLists.find((candidate) => candidate.id === view.smartListId)?.sort ?? [];
}

function getTaskGroupKeys(task: Task, groupBy: TaskGroupBy): string[] {
  switch (groupBy) {
    case 'none':
      return ['open'];
    case 'tag':
      return task.tags.length > 0 ? task.tags : ['No tags'];
    case 'project':
      return [task.projectId];
    case 'priority':
      return [String(task.priority)];
    case 'due':
      return [dateGroupKey(task.dueAt)];
    case 'scheduled':
      return [dateGroupKey(task.scheduledAt)];
    case 'workflow':
      return [task.workflowState];
    case 'estimate':
      return [task.estimateMinutes === undefined ? 'No Estimate' : String(task.estimateMinutes)];
    case 'owner':
      return [task.owner || 'No owner'];
  }
}

function getGroupLabel(key: string, groupBy: TaskGroupBy, projects: Project[]): string {
  if (groupBy === 'project') return projects.find((project) => project.id === key)?.name ?? key;
  if (groupBy === 'priority') return key === '0' ? 'No priority' : `P${key}`;
  if (groupBy === 'due' || groupBy === 'scheduled') return key;
  if (groupBy === 'estimate') return formatEstimateGroupLabel(key);
  if (groupBy === 'tag') return key === 'No tags' ? key : `#${key}`;
  return key;
}

function compareGroupKeys(
  left: string,
  right: string,
  groupBy: TaskGroupBy,
  projectNames: Map<string, string>,
): number {
  if (groupBy === 'project') {
    return (
      (projectNames.get(left) ?? left).localeCompare(projectNames.get(right) ?? right, undefined, {
        sensitivity: 'base',
      }) || left.localeCompare(right)
    );
  }
  if (groupBy === 'priority') {
    return prioritySortValue(Number(left)) - prioritySortValue(Number(right));
  }
  if (groupBy === 'estimate') return estimateGroupValue(left) - estimateGroupValue(right);
  return left.localeCompare(right, undefined, { sensitivity: 'base' });
}

function compareTaskSortKey(left: Task, right: Task, key: TaskSort['key']): number {
  switch (key) {
    case 'manual':
      return (
        (left.manualOrder ?? Number.MAX_SAFE_INTEGER) -
          (right.manualOrder ?? Number.MAX_SAFE_INTEGER) || left.createdAt - right.createdAt
      );
    case 'name':
      return left.title.localeCompare(right.title, undefined, { sensitivity: 'base' });
    case 'priority':
      return prioritySortValue(left.priority) - prioritySortValue(right.priority);
    case 'due':
      return compareOptionalNumber(left.dueAt, right.dueAt);
    case 'scheduled':
      return compareOptionalNumber(left.scheduledAt, right.scheduledAt);
    case 'tag':
      return (left.tags[0] ?? '').localeCompare(right.tags[0] ?? '', undefined, {
        sensitivity: 'base',
      });
    case 'project':
      return left.projectId.localeCompare(right.projectId);
    case 'estimate':
      return (left.estimateMinutes ?? 0) - (right.estimateMinutes ?? 0);
    case 'created':
      return left.createdAt - right.createdAt;
    case 'updated':
      return left.updatedAt - right.updatedAt;
  }
}

function compareOptionalNumber(left: number | undefined, right: number | undefined): number {
  return (left ?? Number.MAX_SAFE_INTEGER) - (right ?? Number.MAX_SAFE_INTEGER);
}

function estimateGroupValue(value: string): number {
  return value === 'No Estimate' ? 0 : Number(value);
}

function formatEstimateGroupLabel(value: string): string {
  if (value === 'No Estimate') return value;
  const minutes = Number(value);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes === 0 ? `${hours} hr` : `${hours} hr ${remainingMinutes} min`;
}

function prioritySortValue(value: number): number {
  if (value === 1) return 3;
  if (value === 2) return 2;
  if (value === 3) return 1;
  return 0;
}

// Hoisted: dateGroupKey runs per task when grouping by date, and constructing
// Intl.DateTimeFormat per call is expensive.
const DATE_GROUP_FORMAT = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });

function dateGroupKey(value: number | undefined): string {
  if (value === undefined) return 'No date';
  return DATE_GROUP_FORMAT.format(new Date(value));
}
